"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";

import type { EventMetadata } from "@soonlist/cal";

import {
  Globe,
  Lock,
  MapPinned,
  PenSquare,
  ScanText,
} from "~/components/icons";
import { getGoogleMapsUrl } from "~/lib/maps";
import { cn } from "~/lib/utils";
import { EventMetadataDisplay } from "./EventDisplays";
import { EventEnrichmentSection } from "./EventEnrichment";
import LightboxImage from "./LightboxImage";
import { SupporterPill } from "./SupporterBadge";

export default function EventCard(props: {
  eventId: string;
  eventImage: string | null;
  eventTitle?: string;
  eventName?: string;
  eventDate: string;
  eventTime?: string | React.ReactNode;
  eventTimeInZone?: string | React.ReactNode;
  eventLocation: string;
  eventDescription: string;
  eventShortDescription?: string;
  eventLink?: string;
  userUsername?: string;
  userAvatar: string;
  userName: string;
  userDisplayName?: string;
  userEmoji?: string;
  userIsSupporter?: boolean;
  hasUser?: boolean;
  isPrivate?: boolean;
  isOwner?: boolean;
  editHref?: string;
  brand?: React.ReactNode;
  shareButton: React.ReactNode;
  calendarButton?: React.ReactNode;
  followButton?: React.ReactNode;
  editButton?: React.ReactNode;
  deleteButton?: React.ReactNode;
  eventMetadata?: EventMetadata;
}) {
  const {
    eventId,
    eventImage,
    eventTitle,
    eventName,
    eventDate,
    eventTime,
    eventTimeInZone,
    eventLocation,
    eventDescription,
    eventShortDescription,
    eventLink,
    userUsername,
    userAvatar,
    userName,
    userDisplayName,
    userEmoji,
    userIsSupporter,
    hasUser,
    isPrivate,
    isOwner,
    editHref,
    brand,
    shareButton,
    calendarButton,
    followButton,
    editButton,
    deleteButton,
    eventMetadata,
  } = props;

  const displayName = userDisplayName || (userName ? userName : "");
  const trimmedLocation = eventLocation.trim();
  const mapsUrl = trimmedLocation ? getGoogleMapsUrl(trimmedLocation) : "";

  const lastChar = eventId.charCodeAt(eventId.length - 1) || 0;
  const imageRotation = lastChar % 2 === 0 ? "10deg" : "-10deg";

  const hasCaptureText = eventDescription.trim().length > 0;
  const showCapturePanel = !!eventImage || hasCaptureText;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <div
        className={cn(
          "flex flex-wrap items-center gap-2",
          brand ? "justify-between" : "justify-end",
        )}
      >
        {brand}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {calendarButton}
          {shareButton}
          {followButton}
          {editButton}
          {deleteButton}
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-3xl bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-lg font-medium text-neutral-2">
              {eventDate}
              {eventTime ? <> • {eventTime}</> : null}
            </p>
            {eventTimeInZone ? (
              <div className="flex items-center gap-1">
                <Globe
                  className="size-3.5 shrink-0 text-neutral-2"
                  aria-hidden="true"
                />
                <span className="text-base font-medium italic text-neutral-2">
                  {eventTimeInZone}
                </span>
              </div>
            ) : null}
          </div>

          <h1 className="text-3xl font-bold text-neutral-1">
            {eventTitle || eventName}
          </h1>

          {eventShortDescription?.trim() ? (
            <div className="-mt-1 flex items-center gap-1">
              <ScanText
                className="size-3.5 shrink-0 text-neutral-2"
                aria-hidden="true"
              />
              <span className="flex-1 text-base font-medium text-neutral-2">
                {eventShortDescription}
              </span>
            </div>
          ) : null}

          {trimmedLocation && mapsUrl ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-base font-semibold text-interactive-1 transition-colors hover:underline"
              aria-label={`Open ${trimmedLocation} in maps`}
            >
              <MapPinned className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0">{trimmedLocation}</span>
            </a>
          ) : null}
        </div>

        {showCapturePanel ? (
          <div className="overflow-hidden rounded-[28px] border border-white/80 bg-[#EDE8FF]">
            <div className="flex items-center gap-3.5 p-3.5">
              {eventImage ? (
                <div
                  className="shrink-0"
                  style={{ transform: `rotate(${imageRotation})` }}
                >
                  <div className="relative h-[120px] w-[67.5px] overflow-hidden rounded-2xl border-[3px] border-white bg-white">
                    <LightboxImage
                      src={eventImage}
                      alt="Event flyer"
                      fill
                      objectFit="cover"
                      compact
                      sizes="68px"
                    />
                  </div>
                </div>
              ) : null}

              {hasCaptureText ? (
                <p className="flex-1 select-text whitespace-pre-line text-[15px] leading-[22px] text-neutral-1">
                  {eventDescription}
                </p>
              ) : null}
            </div>

            {hasUser ? (
              <div className="border-t border-white/70 bg-white/60 p-3.5">
                <CapturedByFooter
                  username={userUsername}
                  displayName={displayName}
                  userAvatar={userAvatar}
                  userEmoji={userEmoji}
                  userIsSupporter={userIsSupporter}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {eventMetadata ? (
          <EventMetadataDisplay eventMetadata={eventMetadata} />
        ) : null}

        {eventLink ? (
          <a
            href={eventLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block self-start rounded-md bg-interactive-1 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-interactive-2"
          >
            View Event
          </a>
        ) : null}

        {isOwner && editHref ? (
          <div className="flex items-center justify-center gap-1.5 border-t border-neutral-200 pt-4">
            <Link
              href={editHref}
              className="flex items-center gap-1.5 text-sm font-medium text-neutral-2 transition-colors hover:text-neutral-1"
            >
              <PenSquare className="size-3.5" aria-hidden="true" />
              Edit details
            </Link>
          </div>
        ) : null}
      </div>

      {isPrivate ? (
        <div className="flex flex-wrap gap-1.5 px-1">
          <span className="flex items-center gap-1.5 rounded-full border border-neutral-3 bg-white px-2.5 py-1.5 text-xs font-medium text-neutral-1">
            <Lock className="size-3 text-neutral-2" aria-hidden="true" />
            Private
          </span>
        </div>
      ) : null}

      <EventEnrichmentSection eventId={eventId} />

      {eventImage ? (
        <div className="rounded-3xl bg-white p-4 shadow-sm">
          <div className="overflow-hidden rounded-2xl">
            <LightboxImage
              src={eventImage}
              alt="Event flyer"
              width={1284}
              height={1284}
              objectFit="contain"
              className={cn("h-auto w-full")}
              sizes="(max-width: 672px) 100vw, 672px"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CapturedByFooter({
  username,
  displayName,
  userAvatar,
  userEmoji,
  userIsSupporter,
}: {
  username?: string;
  displayName: string;
  userAvatar: string;
  userEmoji?: string;
  userIsSupporter?: boolean;
}) {
  const inner = (
    <div className="flex items-center gap-2.5">
      <div className="relative shrink-0">
        <div className="h-8 w-8 overflow-hidden rounded-full border-2 border-interactive-1">
          {userAvatar ? (
            <Image
              src={userAvatar}
              alt="User avatar"
              width={32}
              height={32}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
        {userEmoji ? (
          <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-interactive-1">
            <span className="text-[11px]">{userEmoji}</span>
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-xs font-medium text-neutral-2">Captured by</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="text-sm font-semibold text-neutral-1">{displayName}</p>
          {userIsSupporter ? <SupporterPill /> : null}
        </div>
      </div>
    </div>
  );

  if (!username) return inner;

  return (
    <Link
      href={`/${username}`}
      className="block transition-opacity hover:opacity-80"
      aria-label={`Captured by ${displayName}`}
    >
      {inner}
    </Link>
  );
}
